import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, API } from "../../lib/api";
import { PageHeader, inputCls, btnGhost, EmptyState } from "../../components/bits";
import { Download, Search, FileUp } from "lucide-react";
import { CircularForm } from "../../components/admin/CircularForm";
import { CircularTable } from "../../components/admin/CircularTable";
import { CircularEditModal } from "../../components/admin/CircularEditModal";

export const HIJRI_MONTHS = ["محرم", "صفر", "ربيع الأول", "ربيع الآخر", "جمادى الأولى", "جمادى الآخرة", "رجب", "شعبان", "رمضان", "شوال", "ذو القعدة", "ذو الحجة"];

export function normHijri(d) {
  const parts = String(d || "").replace(/[.\-]/g, "/").split("/").map((p) => p.trim()).filter(Boolean);
  if (parts.length !== 3) return null;
  let [a, b, c] = parts;
  let y, m, day;
  if (a.length === 4) { [y, m, day] = [a, b, c]; } else { [y, m, day] = [c, b, a]; }
  if (!/^\d{4}$/.test(y) || !/^\d{1,2}$/.test(m) || !/^\d{1,2}$/.test(day)) return null;
  return `${y}/${m.padStart(2, "0")}/${day.padStart(2, "0")}`;
}

export function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} ك.ب`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} م.ب`;
}

export function fmtHijri(d) {
  return d ? `${d} هـ` : "—";
}

export default function AdminCirculars() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("");
  const [form, setForm] = useState({ title: "", issue_date: "" });
  const [editModal, setEditModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const editFileRef = useRef(null);
  const importRef = useRef(null);

  const load = useCallback(() => api.get("/admin/circulars").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const nd = normHijri(form.issue_date);
    if (!form.title.trim()) { toast.error("أدخل عنوان التعميم"); return; }
    if (!nd) { toast.error("أدخل تاريخ الإصدار الهجري بصيغة صحيحة مثل 24/03/1448"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("issue_date", nd);
      const f = fileRef.current?.files?.[0];
      if (f) fd.append("file", f);
      await api.post("/admin/circulars", fd);
      toast.success("تمت إضافة التعميم وإتاحته لجميع المنسقين");
      setForm({ title: "", issue_date: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    const nd = normHijri(editModal.issue_date);
    if (!editModal.title.trim() || !nd) { toast.error("تأكد من العنوان والتاريخ"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", editModal.title.trim());
      fd.append("issue_date", nd);
      const f = editFileRef.current?.files?.[0];
      if (f) fd.append("file", f);
      await api.put(`/admin/circulars/${editModal.id}`, fd);
      toast.success("تم تحديث التعميم");
      setEditModal(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`حذف التعميم «${c.title}»؟`)) return;
    try {
      await api.delete(`/admin/circulars/${c.id}`);
      toast.success("تم حذف التعميم");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/circulars/import", fd);
      toast.success(`استُورد ${data.imported} تعميماً (مكرر ${data.duplicates} / مرفوض ${data.rejected.length})`);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); if (importRef.current) importRef.current.value = ""; }
  };

  const filtered = (items || []).filter((c) =>
    (!q || c.title.includes(q)) && (!month || (c.issue_date || "").split("/")[1] === month));

  if (items === null) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  return (
    <div data-testid="admin-circulars-page">
      <PageHeader title="التعاميم الصادرة" sub="أضف التعاميم الصادرة من الإدارة لتصل جميع المنسقين تلقائياً — مرتبة حسب تاريخ الإصدار">
        <input ref={importRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={doImport} data-testid="circular-import-file" />
        <button data-testid="circular-import-btn" onClick={() => importRef.current?.click()} disabled={busy} className={btnGhost}>
          <FileUp className="w-4 h-4" /> استيراد
        </button>
        <button data-testid="circular-export-btn" onClick={() => window.open(`${API}/admin/circulars/export`, "_blank")} className={btnGhost}>
          <Download className="w-4 h-4" /> تصدير Excel
        </button>
      </PageHeader>

      <CircularForm form={form} setForm={setForm} fileRef={fileRef} busy={busy} onAdd={add} />

      <div className="flex gap-3 mb-5 flex-wrap">
        <select data-testid="circular-month-filter" value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} w-40`}>
          <option value="">كل الأشهر</option>
          {HIJRI_MONTHS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
        </select>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute right-3 top-3 text-mutext" />
          <input data-testid="circular-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث في عناوين التعاميم…" className={`${inputCls} pr-9`} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState testid="circulars-empty" text="لا توجد تعاميم مطابقة" />
      ) : (
        <CircularTable filtered={filtered} onEdit={(c) => setEditModal({ ...c })} onRemove={remove} />
      )}

      <CircularEditModal editModal={editModal} setEditModal={setEditModal} editFileRef={editFileRef} busy={busy} onSave={saveEdit} />
    </div>
  );
}
